import React, { useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Badge } from "./ui/badge";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "./ui/dropdown-menu";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "./ui/alert-dialog";
import { MoreHorizontal, Mail, Send, Ban, UserX, UserCheck, RefreshCw, Loader2, ShieldOff } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const TONE_CLASSES = {
  muted:  "bg-muted text-muted-foreground hover:bg-muted",
  amber:  "bg-amber-100 text-amber-800 hover:bg-amber-100 dark:bg-amber-900/30 dark:text-amber-300",
  blue:   "bg-sky-100 text-sky-800 hover:bg-sky-100 dark:bg-sky-900/30 dark:text-sky-300",
  red:    "bg-red-100 text-red-800 hover:bg-red-100 dark:bg-red-900/30 dark:text-red-300",
  green:  "bg-emerald-100 text-emerald-800 hover:bg-emerald-100 dark:bg-emerald-900/30 dark:text-emerald-300",
};

/**
 * Props:
 *   portalStatus: {status, label, tone, invitation_id, user_id}
 *   targetType:   'client' | 'employee'
 *   targetId:     UUID string
 *   targetEmail:  string (optional — disables Send if missing)
 *   onChanged:    () => void  // parent refetch
 *   compact:      bool — render badge only, no actions menu
 */
export default function PortalStatusBadge({ portalStatus, targetType, targetId, targetEmail, onChanged, compact = false }) {
  const [busy, setBusy] = useState(false);
  const [confirmAction, setConfirmAction] = useState(null);

  if (!portalStatus) return null;
  const { status, label, tone } = portalStatus;
  const toneClass = TONE_CLASSES[tone] || TONE_CLASSES.muted;
  const hasEmail = !!targetEmail;

  const doAction = async (action) => {
    setBusy(true);
    try {
      const res = await axios.post(`${API}/portal/status-action`, {
        target_type: targetType,
        target_id: targetId,
        action,
      });
      const msg = {
        send_invite: "Invitation sent",
        resend_invite: "Invitation re-sent",
        revoke_invite: "Invitation revoked",
        deactivate_user: "Portal access deactivated",
        reactivate_user: "Portal access reactivated",
      }[action] || "Done";
      toast.success(msg);
      onChanged && onChanged();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Action failed");
    } finally {
      setBusy(false);
      setConfirmAction(null);
    }
  };

  // Compact mode — read-only badge (used in dense list rows)
  if (compact) {
    return (
      <Badge variant="secondary" className={`${toneClass} font-medium`} data-testid={`portal-status-${status}`}>
        {label}
      </Badge>
    );
  }

  // Determine which actions to show based on current status
  const canSend       = status === "no_access" && hasEmail;
  const canResend     = ["pending", "sent", "expired"].includes(status) && hasEmail;
  const canRevoke     = ["pending", "sent", "expired"].includes(status);
  const canDeactivate = status === "active";
  const canReactivate = status === "deactivated";

  return (
    <>
      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <button
            type="button"
            disabled={busy}
            className={`inline-flex items-center gap-1.5 rounded-full px-2.5 py-0.5 text-xs font-medium transition-all hover:opacity-80 ${toneClass} ${busy ? "opacity-50 cursor-wait" : "cursor-pointer"}`}
            data-testid={`portal-status-trigger-${status}`}
            aria-label={`Portal status: ${label}. Click to manage.`}
          >
            {busy ? <Loader2 className="h-3 w-3 animate-spin" /> : <MoreHorizontal className="h-3 w-3" />}
            {label}
          </button>
        </DropdownMenuTrigger>
        <DropdownMenuContent align="end" className="w-56">
          <DropdownMenuLabel className="text-xs">Portal access</DropdownMenuLabel>
          <DropdownMenuSeparator />
          {canSend && (
            <DropdownMenuItem onClick={() => doAction("send_invite")} data-testid="action-send-invite">
              <Send className="h-4 w-4 mr-2" />
              Send portal invite
            </DropdownMenuItem>
          )}
          {canResend && (
            <DropdownMenuItem onClick={() => doAction("resend_invite")} data-testid="action-resend-invite">
              <RefreshCw className="h-4 w-4 mr-2" />
              Resend invitation
            </DropdownMenuItem>
          )}
          {canRevoke && (
            <DropdownMenuItem
              onClick={() => setConfirmAction("revoke_invite")}
              className="text-red-600 focus:text-red-700 focus:bg-red-50"
              data-testid="action-revoke-invite"
            >
              <Ban className="h-4 w-4 mr-2" />
              Revoke invitation
            </DropdownMenuItem>
          )}
          {canDeactivate && (
            <DropdownMenuItem
              onClick={() => setConfirmAction("deactivate_user")}
              className="text-red-600 focus:text-red-700 focus:bg-red-50"
              data-testid="action-deactivate-user"
            >
              <UserX className="h-4 w-4 mr-2" />
              Deactivate access
            </DropdownMenuItem>
          )}
          {canReactivate && (
            <DropdownMenuItem onClick={() => doAction("reactivate_user")} data-testid="action-reactivate-user">
              <UserCheck className="h-4 w-4 mr-2" />
              Reactivate access
            </DropdownMenuItem>
          )}
          {!canSend && !canResend && !canRevoke && !canDeactivate && !canReactivate && (
            <DropdownMenuItem disabled>
              <ShieldOff className="h-4 w-4 mr-2" />
              No actions available
            </DropdownMenuItem>
          )}
          {!hasEmail && status === "no_access" && (
            <DropdownMenuItem disabled className="text-xs">
              <Mail className="h-3 w-3 mr-2" />
              Add an email to enable invites
            </DropdownMenuItem>
          )}
        </DropdownMenuContent>
      </DropdownMenu>

      <AlertDialog open={!!confirmAction} onOpenChange={(o) => { if (!o) setConfirmAction(null); }}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>
              {confirmAction === "revoke_invite" && "Revoke portal invitation?"}
              {confirmAction === "deactivate_user" && "Deactivate portal access?"}
            </AlertDialogTitle>
            <AlertDialogDescription>
              {confirmAction === "revoke_invite" && "The invitation link will stop working. You can send a new invitation later."}
              {confirmAction === "deactivate_user" && "The user will no longer be able to sign in to their portal. You can reactivate them later."}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={() => doAction(confirmAction)}
              className="bg-red-600 hover:bg-red-700"
              data-testid="confirm-portal-action"
            >
              Confirm
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  );
}
